import { useEffect, useState } from "react";
import dicomParser from "dicom-parser";

const dicomImageCache = new Map();
const MAX_DICOM_CACHE_SIZE = 36;
const JPEG_TRANSFER_SYNTAXES = new Set([
  "1.2.840.10008.1.2.4.50",
  "1.2.840.10008.1.2.4.51",
]);

export const isDicomSource = (source = "") => {
  const value = String(source).trim();
  return /\.dcm(?:$|[?#])/i.test(value) || /^data:application\/dicom/i.test(value);
};

const clampByte = (value) => Math.max(0, Math.min(255, Math.round(value)));

const canvasToDataUrl = (canvas) => canvas.toDataURL("image/png");

const renderJpegFrame = (dataSet, pixelElement) =>
  new Promise((resolve, reject) => {
    const frame = dicomParser.readEncapsulatedImageFrame(dataSet, pixelElement, 0);
    const objectUrl = URL.createObjectURL(new Blob([frame], { type: "image/jpeg" }));
    const image = new Image();

    image.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = image.naturalWidth || image.width;
      canvas.height = image.naturalHeight || image.height;
      canvas.getContext("2d").drawImage(image, 0, 0);
      URL.revokeObjectURL(objectUrl);
      resolve(canvasToDataUrl(canvas));
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("The browser could not decode this DICOM JPEG frame."));
    };
    image.src = objectUrl;
  });

const renderUncompressedFrame = (dataSet, pixelElement, transferSyntax) => {
  const rows = dataSet.uint16("x00280010");
  const columns = dataSet.uint16("x00280011");
  const samplesPerPixel = dataSet.uint16("x00280002") || 1;
  const bitsAllocated = dataSet.uint16("x00280100") || 8;
  const bitsStored = dataSet.uint16("x00280101") || bitsAllocated;
  const highBit = dataSet.uint16("x00280102") ?? bitsStored - 1;
  const signed = dataSet.uint16("x00280103") === 1;
  const planar = dataSet.uint16("x00280006") === 1;
  const photometric = (dataSet.string("x00280004") || "MONOCHROME2").trim();

  if (!rows || !columns || !pixelElement) {
    throw new Error("DICOM image dimensions or pixel data are missing.");
  }
  if (bitsAllocated !== 8 && bitsAllocated !== 16) {
    throw new Error(`Unsupported DICOM bit depth: ${bitsAllocated}.`);
  }

  const sampleCount = rows * columns * samplesPerPixel;
  const bytesPerSample = bitsAllocated / 8;
  const availableBytes = pixelElement.length || 0;
  if (availableBytes < sampleCount * bytesPerSample) {
    throw new Error("The DICOM pixel data is incomplete or compressed.");
  }

  const bytes = dataSet.byteArray;
  const view = new DataView(
    bytes.buffer,
    bytes.byteOffset + pixelElement.dataOffset,
    availableBytes
  );
  const littleEndian = transferSyntax !== "1.2.840.10008.1.2.2";
  const shift = Math.max(0, highBit - bitsStored + 1);
  const valueRange = 2 ** bitsStored;
  const valueMask = bitsStored === 16 ? 0xffff : valueRange - 1;
  const signBit = 2 ** (bitsStored - 1);

  const readSample = (index) => {
    const offset = index * bytesPerSample;
    let value = bitsAllocated === 16
      ? view.getUint16(offset, littleEndian)
      : view.getUint8(offset);
    value = (value >> shift) & valueMask;
    if (signed && value >= signBit) value -= valueRange;
    return value;
  };

  const canvas = document.createElement("canvas");
  canvas.width = columns;
  canvas.height = rows;
  const context = canvas.getContext("2d");
  const imageData = context.createImageData(columns, rows);
  const output = imageData.data;
  const pixelCount = rows * columns;

  if (samplesPerPixel >= 3 && !photometric.startsWith("MONOCHROME")) {
    const colorSample = (pixelIndex, channel) => {
      const sampleIndex = planar
        ? channel * pixelCount + pixelIndex
        : pixelIndex * samplesPerPixel + channel;
      const value = readSample(sampleIndex);
      return bitsStored > 8 ? value / (2 ** (bitsStored - 8)) : value;
    };

    for (let index = 0; index < pixelCount; index += 1) {
      let red = colorSample(index, 0);
      let green = colorSample(index, 1);
      let blue = colorSample(index, 2);

      if (photometric.startsWith("YBR")) {
        const luminance = red;
        const cb = green - 128;
        const cr = blue - 128;
        red = luminance + 1.402 * cr;
        green = luminance - 0.344136 * cb - 0.714136 * cr;
        blue = luminance + 1.772 * cb;
      }

      const outputIndex = index * 4;
      output[outputIndex] = clampByte(red);
      output[outputIndex + 1] = clampByte(green);
      output[outputIndex + 2] = clampByte(blue);
      output[outputIndex + 3] = 255;
    }
  } else {
    const slope = dataSet.floatString("x00281053") ?? 1;
    const intercept = dataSet.floatString("x00281052") ?? 0;
    let windowCenter = dataSet.floatString("x00281050");
    let windowWidth = dataSet.floatString("x00281051");

    if (!Number.isFinite(windowCenter) || !Number.isFinite(windowWidth) || windowWidth <= 1) {
      let minimum = Infinity;
      let maximum = -Infinity;
      for (let index = 0; index < pixelCount; index += 1) {
        const value = readSample(index) * slope + intercept;
        minimum = Math.min(minimum, value);
        maximum = Math.max(maximum, value);
      }
      windowCenter = (minimum + maximum) / 2;
      windowWidth = Math.max(2, maximum - minimum);
    }

    const lower = windowCenter - 0.5 - (windowWidth - 1) / 2;
    const upper = windowCenter - 0.5 + (windowWidth - 1) / 2;
    const invert = photometric === "MONOCHROME1";

    for (let index = 0; index < pixelCount; index += 1) {
      const value = readSample(index) * slope + intercept;
      let intensity;
      if (value <= lower) intensity = 0;
      else if (value > upper) intensity = 255;
      else intensity = ((value - (windowCenter - 0.5)) / (windowWidth - 1) + 0.5) * 255;
      if (invert) intensity = 255 - intensity;

      const outputIndex = index * 4;
      const byte = clampByte(intensity);
      output[outputIndex] = byte;
      output[outputIndex + 1] = byte;
      output[outputIndex + 2] = byte;
      output[outputIndex + 3] = 255;
    }
  }

  context.putImageData(imageData, 0, 0);
  return canvasToDataUrl(canvas);
};

const decodeDicom = async (source) => {
  const response = await fetch(source);
  if (!response.ok) {
    throw new Error(`Unable to download DICOM image (${response.status}).`);
  }

  const dataSet = dicomParser.parseDicom(new Uint8Array(await response.arrayBuffer()));
  const pixelElement = dataSet.elements.x7fe00010;
  const transferSyntax = (dataSet.string("x00020010") || "1.2.840.10008.1.2").trim();

  if (pixelElement?.encapsulatedPixelData) {
    if (JPEG_TRANSFER_SYNTAXES.has(transferSyntax)) {
      return renderJpegFrame(dataSet, pixelElement);
    }
    throw new Error(`Unsupported compressed DICOM transfer syntax: ${transferSyntax}.`);
  }

  return renderUncompressedFrame(dataSet, pixelElement, transferSyntax);
};

export const loadRenderableImageSource = (source) => {
  if (!source || !isDicomSource(source)) return Promise.resolve(source);

  const cacheKey = String(source);
  if (dicomImageCache.has(cacheKey)) {
    const cached = dicomImageCache.get(cacheKey);
    dicomImageCache.delete(cacheKey);
    dicomImageCache.set(cacheKey, cached);
    return cached;
  }

  const request = decodeDicom(cacheKey).catch((error) => {
    dicomImageCache.delete(cacheKey);
    throw error;
  });
  dicomImageCache.set(cacheKey, request);

  while (dicomImageCache.size > MAX_DICOM_CACHE_SIZE) {
    const oldestKey = dicomImageCache.keys().next().value;
    dicomImageCache.delete(oldestKey);
  }

  return request;
};

export const useRenderableImageSource = (source) => {
  const dicom = isDicomSource(source);
  const [state, setState] = useState(() => ({
    src: dicom ? "" : source,
    loading: dicom,
    error: null,
  }));

  useEffect(() => {
    let cancelled = false;
    const isDicom = isDicomSource(source);
    setState({ src: isDicom ? "" : source, loading: isDicom, error: null });

    loadRenderableImageSource(source)
      .then((src) => {
        if (!cancelled) setState({ src, loading: false, error: null });
      })
      .catch((error) => {
        if (!cancelled) setState({ src: "", loading: false, error });
      });

    return () => {
      cancelled = true;
    };
  }, [source]);

  return state;
};
